import React, { useState, useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getAuthToken,
  saveAuthorizationToken,
} from '../../latest/src/utils/Authentications';
import {
  authUser,
  updateLoggedInUser,
} from '../../redux/modules/authentication';
import WrapperBackground from '../../components/base/WrapperBackground/WrapperBackground';
import Status from '../../components/base/Status/Status';
import Storage from '../../latest/src/utils/Storage';
import Spinner from 'react-bootstrap/Spinner';
import './app-loading.scss';
import { UserServiceSection } from '../../latest/src/constants/AppServerConfig';
import { fetchUserInfoV1 } from '../../api/user';
import { Logger } from 'src/latest/src/services';
import {
  isPrivateRoute,
  isPublicRoute,
  isShouldLoggoutAndRedirectRoute,
  isShouldLoggoutRoute,
  isSignedInRedirectRoute,
  isTokenBasedRoute,
} from 'src/latest/src/constants/AppRoutesExpressions';
import {
  clearAccessToken,
  hasSessionHint,
  isMemoryOnlyToken,
  purgeSessionStorage,
  refreshAccessToken,
} from 'src/latest/src/auth/authStore';
import { StorageKey } from 'src/latest/src/constants/StorageKey';
import {
  gateSessionOn,
  withStartupTimeout,
} from 'src/latest/src/auth/sessionStartup';

// The token endpoint answers a missing or expired refresh credential with 400
// invalid_grant rather than 401. Anything else — a network drop, a 5xx, a
// timeout — is not evidence that the user is logged out, so it must not send
// them to /login. Phase 0 pins this contract with the Auth Server.
const isLoggedOutResponse = error =>
  [400, 401].includes(error?.response?.status);

type Props = {
  children: any,
  route: string,
};

export default function AppLoadingContainer({
  children,
}: Props): any | React$Element<'div'> {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const history = useNavigate();
  const [isAppReady, setAppReady] = useState(false);
  const [isErrorLoaded, setErrorLoaded] = useState(false);

  useEffect(() => {
    setErrorLoaded(false);

    const path = window.location.pathname;

    // /providers short-circuits startup entirely, which is fine while the
    // session is readable from storage. With a memory-only one, startup is where
    // a signed-in visitor gets redirected, so the redirect routes opt back in —
    // startup itself skips the refresh when no session was ever held here.
    // /sign-up and /therapist-user-signup keep skipping it; nothing redirects
    // away from those.
    const needsStartup =
      !isShouldLoggoutAndRedirectRoute(path) ||
      (isMemoryOnlyToken && isSignedInRedirectRoute(path));

    if (needsStartup) {
      checkAuth();
    } else {
      setAppReady(true);
    }
  }, []);

  useEffect(() => {
    const excludedURLs = [
      'initquestions',
      'error',
      'sign-up',
      'login',
      'greetings',
      'opt-out',
      'redirect',
      'logout',
    ];
    const matchedUrl = excludedURLs.filter(url =>
      window.location.pathname?.includes(url),
    );

    const urlMatched =
      matchedUrl.length !== 0 && window.location.pathname !== '/';

    try {
      if (!urlMatched) {
        if (Storage.getItem('qrCode')) {
          history(Storage.getItem('signup-url'));
        }
      }
    } catch (error) {
      Logger.log('AppLoadingContainer', { error });
    }
  }, [window?.location?.pathname]);

  const checkAuth = async () => {
    try {
      if (isMemoryOnlyToken) {
        await resumeSessionByRefresh();
      } else {
        await resumeSessionFromStorage();
      }

      setAppReady(true);
    } catch (err) {
      setAppReady(true);
      console.error('Error while checking auth', err);
    }
  };

  /**
   * Phase 3: re-establish the session by refreshing, not by reading storage.
   * Memory is empty on every load, so without this a reload would be a logout.
   * Phase 4 moves the refresh credential into the HttpOnly cookie; nothing in
   * here changes when it does, because authStore owns that call.
   */
  const resumeSessionByRefresh = async () => {
    const path = window.location.pathname;

    // Whatever an earlier build persisted — token, profile, isAuthed — is read
    // by nothing now, so it goes on the first load after the switch. Runs before
    // the route checks below: a returning visitor on a public page should have
    // their old session material cleared too.
    purgeSessionStorage();

    // The 2FA and signup screens are mid-flow and own their own state.
    if (Storage.getItem('required2FA') || Storage.getItem('signup-url')) {
      return;
    }

    // A token-based link (/verification, /intake, /_payment-consents) carries
    // its own credential. A page meant to be read signed out that nobody is
    // redirected away from has no session worth asking for either, which keeps
    // /sign-up, /reset-password, /verify-onboarding and /error at zero auth
    // calls on load.
    if (
      isTokenBasedRoute(path) ||
      (isShouldLoggoutRoute(path) && !isSignedInRedirectRoute(path))
    ) {
      return;
    }

    // The remaining public pages also render fine signed out, so they refresh
    // only when this browser has held a session: to restore it on /profile or
    // /change-password, or to redirect a signed-in visitor off /login or
    // /providers. A visitor who never signed in makes no auth call at all.
    // Private pages always refresh — they cannot work without a session.
    if (isPublicRoute(path) && !hasSessionHint()) {
      return;
    }

    try {
      // Gated so requests from mounting components wait for the token instead
      // of 401ing in a herd. The timeout sits inside the gate, so a hung refresh
      // releases those waiting requests as well as the spinner — the other way
      // round, every API call made during startup would wait on it forever.
      await gateSessionOn(withStartupTimeout(refreshAccessToken()));
    } catch (error) {
      const loggedOut = isLoggedOutResponse(error);

      if (loggedOut) {
        // The server says there is no session, so the marker is stale. Dropping
        // it keeps the next visit to a public page free of auth calls.
        clearAccessToken();
      } else {
        Logger.error('Session startup failed', error);
      }

      // A public page works without a session — /login, /providers, and a
      // provider profile opened in a new tab — so failing to find one, for any
      // reason, just means rendering it signed out. Showing the error screen
      // there would put it in front of the login form exactly when something
      // is already going wrong. Only a private page, which cannot work without
      // a session, goes to /login or to the error screen.
      if (!isPublicRoute(path)) {
        if (loggedOut) {
          history('/login');
        } else {
          setErrorLoaded(true);
        }
      }

      return;
    }

    const profile = await fetchUserInfoV1(UserServiceSection.profile_display, 'medium');
    const { loggedInUser } = store.getState().authentication;

    let role = profile?.user_type?.includes('THERAPIST')
      ? 'therapist'
      : profile?.user_type?.includes('SCHEDULER')
      ? 'scheduler'
      : 'client';

    // Identity is rebuilt from the profile, but the role the user switched to
    // is a UI choice that has to outlive a reload, so it keeps its own key.
    const activeRole =
      Storage.getItem(StorageKey.activeRole) || loggedInUser?.userRole;

    if (activeRole) role = activeRole;

    const identity = {
      ...profile,
      userRole: role,
      photoUrl: loggedInUser?.photoUrl || '',
    };

    dispatch(authUser(identity));
    dispatch(updateLoggedInUser(identity));

    // Signed in, but sitting on an entry page. Send them to their own dashboard
    // — the active role if they switched to one, otherwise the role the profile
    // says they are. The private-route guard keeps a path that merely contains
    // one of these words (/practice-admin/support) where it is.
    if (isSignedInRedirectRoute(path) && !isPrivateRoute(path)) {
      history(/client|therapist/.test(role) ? `/therapy/${role}` : '/');
    }
  };

  /** Pre-Phase-3 path: the session is whatever localStorage still holds. */
  const resumeSessionFromStorage = async () => {
    const accessToken = getAuthToken();

    const userDetails: Object =
      isShouldLoggoutRoute(window.location.pathname) &&
      Object.keys(accessToken || {}).length === 0 &&
      !window.location.pathname.includes('therapy') &&
      !Storage.getItem('required2FA')
        ? null
        : accessToken || null;

    const { loggedInUser } = store.getState().authentication;
    const storedRole = userDetails?.userRole || loggedInUser?.userRole;
    const storedPhotoUrl =
      userDetails?.photoUrl || loggedInUser?.photoUrl || '';

    if (userDetails) {
      saveAuthorizationToken(userDetails);
    }

    if (
      userDetails?.hasOwnProperty('id') &&
      !Storage.getItem('required2FA') &&
      !Storage.getItem('signup-url')
    ) {
      const profile = await fetchUserInfoV1(UserServiceSection.profile_display, 'medium');

      let role = profile?.user_type?.includes('THERAPIST')
        ? 'therapist'
        : profile?.user_type?.includes('SCHEDULER')
        ? 'scheduler'
        : 'client';

      if (storedRole) role = storedRole;

      dispatch(
        authUser({
          ...profile,
          userRole: role,
          photoUrl: storedPhotoUrl,
        }),
      );
      dispatch(
        updateLoggedInUser({
          ...profile,
          userRole: role,
          photoUrl: storedPhotoUrl,
        }),
      );

      if (
        !isPrivateRoute(window.location.pathname) &&
        !isShouldLoggoutRoute(window.location.pathname) &&
        !isTokenBasedRoute(window.location.pathname)
      ) {
        history(/client|therapist/.test(role) ? `/therapy/${role}` : '/');
      }
    } else {
      if (
        !isShouldLoggoutRoute(window.location.pathname) &&
        !location.pathname.includes('error') &&
        !Storage.getItem('required2FA') &&
        !Storage.getItem('signup-url') &&
        !isPublicRoute(window.location.pathname) &&
        !isTokenBasedRoute(window.location.pathname)
      ) {
        history('/login');
      }
    }
  };

  if (isAppReady) {
    return isErrorLoaded ? (
      <WrapperBackground>
        <div className="app-loading__error">
          <Status status="Page error" variant="light" />
        </div>
      </WrapperBackground>
    ) : (
      children
    );
  }

  return (
    <WrapperBackground className="app-loading">
      <Spinner animation="border" role="status" />
    </WrapperBackground>
  );
}
